import { useState, useEffect, useRef } from 'react';

export const useWebSocket = (roomId, requestedUsername) => {
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(null);
  const [ws, setWs] = useState(null);
  const [uniqueUsername, setUniqueUsername] = useState('');
  
  const reconnectTimeoutRef = useRef(null);
  const initialConnectTimeoutRef = useRef(null);
  const isManuallyDisconnected = useRef(false);
  const isFirstConnection = useRef(true);
  const currentConnectionId = useRef(null);

  useEffect(() => {
    isManuallyDisconnected.current = false;
    isFirstConnection.current = true;
    
    const connect = () => {
      const wsUrl = process.env.REACT_APP_BASE_URL || 'ws://localhost:80/ws';
      const websocket = new WebSocket(
        `${wsUrl}/${roomId}?username=${encodeURIComponent(requestedUsername)}`
      );
      
      const connectionId = Date.now() + Math.random();
      currentConnectionId.current = connectionId;

      websocket.onopen = () => {
        setIsConnected(true);
        
        isFirstConnection.current = false;
        
        if (initialConnectTimeoutRef.current) {
          clearTimeout(initialConnectTimeoutRef.current);
          initialConnectTimeoutRef.current = null;
        }
      };

      websocket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (connectionId !== currentConnectionId.current) {
          return;
        }
        
        if (data.type === 'user_registered') {
          setUniqueUsername(data.unique_username);
        } else {
          setMessages(prev => [...prev, { 
            ...data, 
            id: data.id || Date.now() + Math.random() 
          }]);
        }
      };

      websocket.onclose = () => {
        setIsConnected(false);
        setWs(null);

        if (!isManuallyDisconnected.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 3000);
        }
      };

      websocket.onerror = (error) => {
        setIsConnected(false);
      };

      setWs(websocket);
    };

    connect();

    if (isFirstConnection.current) {
      initialConnectTimeoutRef.current = setTimeout(() => {
        if (isFirstConnection.current && isConnected === null) {
          setIsConnected(false);
        }
      }, 3000);
    }

    return () => {
      isManuallyDisconnected.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (initialConnectTimeoutRef.current) {
        clearTimeout(initialConnectTimeoutRef.current);
      }
      if (ws) {
        ws.close();
      }
    };
  }, [roomId, requestedUsername]);

  const sendMessage = (messageText) => {
    if (messageText.trim() && ws && isConnected) {
      const messageData = {
        type: "message",
        text: messageText.trim()
      };
      
      ws.send(JSON.stringify(messageData));
    }
  };

  return {
    messages,
    isConnected,
    ws,
    uniqueUsername,
    sendMessage
  };
};
